import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';

export type KittenPreviewItem = {
  id?: string | number;
  name?: string;
  description?: string;
  color?: string;
  sex?: string;
  age?: string;
  heroImage?: string;
  images?: string[];
};

type Props = {
  kittens?: KittenPreviewItem[];
};

const fallbackKittens: KittenPreviewItem[] = [
  {
    id: 'aurora',
    name: 'Aurora',
    description: 'Velvety lilac point with a serene, affectionate temperament.',
    color: 'Lilac Point',
    sex: 'Queen',
    age: '12 weeks',
    heroImage: '#',
  },
  {
    id: 'solstice',
    name: 'Solstice',
    description: 'Playful flame point with radiant energy and quick wit.',
    color: 'Flame Point',
    sex: 'Sire',
    age: '10 weeks',
    heroImage: '#',
  },
  {
    id: 'elysian',
    name: 'Elysian',
    description: 'Dreamy blue point who adores cuddles and gentle conversation.',
    color: 'Blue Point',
    sex: 'Queen',
    age: '14 weeks',
    heroImage: '#',
  },
];

export default function KittenPreview({ kittens = [] }: Props) {
  const data = kittens.length ? kittens : fallbackKittens;

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-soft-gold/10 to-white py-24">
      <motion.div
        className="pointer-events-none absolute -top-32 left-1/3 h-72 w-72 rounded-full bg-soft-gold/35 blur-3xl"
        animate={{ opacity: [0.25, 0.45, 0.25], scale: [0.92, 1.05, 0.9] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute top-24 right-[-120px] h-96 w-96 rounded-full bg-amber-200/25 blur-[120px]"
        animate={{ opacity: [0.2, 0.4, 0.2], scale: [0.85, 1.08, 0.9] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col items-center text-center gap-4 mb-16">
          <motion.span
            className="inline-flex items-center gap-2 rounded-full border border-soft-gold/40 bg-white/70 px-5 py-2 text-xs uppercase tracking-[0.4em] text-warm-brown"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Kitten Salon
          </motion.span>
          <motion.h2
            className="text-4xl md:text-5xl font-heading font-bold text-warm-brown"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Meet the current stars of our nursery
          </motion.h2>
          <motion.p
            className="max-w-3xl text-lg text-warm-brown/70"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Each Balinese kitten is a carefully nurtured personality. Explore their emerging temperaments, silken coats, and the gentle rituals they already adore.
          </motion.p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {data.map((kitten, index) => {
            const image = kitten.heroImage || kitten.images?.[0] || '/placeholder_kitten.jpg';
            return (
              <motion.article
                key={kitten.id ?? kitten.name ?? index}
                className="group relative overflow-hidden rounded-3xl border border-white/40 bg-white/80 p-6 backdrop-blur-lg shadow-[0_25px_45px_rgba(204,186,155,0.35)]"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: index * 0.1 }}
                whileHover={{ y: -12, scale: 1.01 }}
              >
                <motion.div
                  className="pointer-events-none absolute inset-0 rounded-3xl"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0.25, 0.45, 0.25] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: index * 0.2 }}
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-soft-gold/20 via-white/25 to-transparent" />
                </motion.div>

                <div className="relative overflow-hidden rounded-2xl">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-t from-warm-brown/30 via-transparent to-transparent opacity-0"
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                  />
                  <Image
                    src={image}
                    alt={kitten.name ? `${kitten.name} preview` : 'Balinese kitten'}
                    width={640}
                    height={480}
                    className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    priority={index === 0}
                  />
                  <motion.span
                    className="pointer-events-none absolute inset-x-6 bottom-4 flex items-center justify-between text-sm text-white"
                    initial={{ opacity: 0, y: 10 }}
                    whileHover={{ opacity: 1, y: 0 }}
                  >
                    <span>{kitten.color || 'Balinese'}</span>
                    <span>{kitten.sex || 'Companion'}</span>
                  </motion.span>
                </div>

                <div className="mt-6 space-y-3 text-left">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-heading font-semibold text-warm-brown">
                      {kitten.name || 'Unnamed Treasure'}
                    </h3>
                    {kitten.age ? (
                      <span className="rounded-full bg-soft-gold/20 px-4 py-1 text-xs font-semibold text-warm-brown/80">
                        {kitten.age}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm leading-relaxed text-warm-brown/70">
                    {kitten.description || 'A curious soul in training, exploring feather wands, purr meditations, and sunny windowsills.'}
                  </p>
                  <div className="flex items-center justify-between pt-2 text-sm">
                    <motion.span
                      className="inline-flex items-center gap-2 text-soft-gold/90"
                      animate={{ opacity: [0.6, 1, 0.6] }}
                      transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: index * 0.3 }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-soft-gold" />
                      Tempered & socialized
                    </motion.span>
                    <Link
                      href="/kittens"
                      className="inline-flex items-center gap-2 rounded-full border border-soft-gold/40 px-4 py-1.5 text-xs font-semibold text-warm-brown transition-all hover:border-soft-gold hover:bg-soft-gold/90 hover:text-white"
                    >
                      View nursery
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
